import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Plus, Pencil, Trash2, X, Check, Package } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { reference: '', designation: '', description: '', prix_ht: '' };

export default function Produits() {
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // produit en cours d'édition
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchProduits = async () => {
    try {
      const res = await api.get('/produits');
      setProduits(res.data);
    } catch { toast.error('Erreur chargement produits'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProduits(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (p) => { setEditing(p); setForm({ reference: p.reference, designation: p.designation, description: p.description || '', prix_ht: p.prix_ht }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.reference || !form.designation || !form.prix_ht) {
      toast.error('Référence, désignation et prix sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/produits/${editing.id}`, { ...form, prix_ht: parseFloat(form.prix_ht) });
        toast.success('Produit mis à jour');
      } else {
        await api.post('/produits', { ...form, prix_ht: parseFloat(form.prix_ht) });
        toast.success('Produit créé');
      }
      setShowModal(false);
      fetchProduits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit définitivement ?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/produits/${id}`);
      toast.success('Produit supprimé');
      setProduits(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setDeletingId(null); }
  };

  return (
    <div className="min-h-screen bg-[hsl(210,20%,98%)] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Package size={24} className="text-[hsl(222,47%,11%)]" />
              Catalogue produits
            </h1>
            <p className="text-gray-500 text-sm mt-1">{produits.length} produit{produits.length > 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-[hsl(222,47%,11%)] text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-[hsl(222,47%,18%)] transition-colors"
            data-testid="add-produit-btn"
          >
            <Plus size={16} /> Ajouter un produit
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400">Chargement...</div>
          ) : produits.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <Package size={40} className="mx-auto mb-3 opacity-30" />
              <p>Aucun produit</p>
            </div>
          ) : (
            <table className="w-full" data-testid="produits-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Référence</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Désignation</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Description</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Prix HT</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {produits.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors" data-testid={`produit-row-${p.id}`}>
                    <td className="px-5 py-4">
                      <span className="font-mono font-bold text-[hsl(222,47%,11%)] text-sm bg-[hsl(222,47%,11%)]/10 px-2 py-1 rounded">
                        {p.reference}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm font-medium text-gray-900">{p.designation}</td>
                    <td className="px-5 py-4 text-sm text-gray-500 hidden md:table-cell">{p.description || '—'}</td>
                    <td className="px-5 py-4 text-right">
                      <span className="font-bold text-gray-900">{p.prix_ht?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                      <span className="text-xs text-gray-400 ml-1">HT</span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => openEdit(p)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          data-testid={`edit-produit-${p.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          disabled={deletingId === p.id}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          data-testid={`delete-produit-${p.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal Ajout / Édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" data-testid="produit-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">{editing ? 'Modifier le produit' : 'Nouveau produit'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Référence *</label>
                <input
                  value={form.reference}
                  onChange={e => setForm(p => ({ ...p, reference: e.target.value.toUpperCase() }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                  placeholder="KLOSS"
                  data-testid="produit-reference-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Désignation *</label>
                <input
                  value={form.designation}
                  onChange={e => setForm(p => ({ ...p, designation: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                  placeholder="KLOSS - Fauteuil coiffure électrique"
                  data-testid="produit-designation-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Description</label>
                <input
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                  placeholder="Description courte..."
                  data-testid="produit-description-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Prix HT (€) *</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.prix_ht}
                  onChange={e => setForm(p => ({ ...p, prix_ht: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                  placeholder="2990.00"
                  data-testid="produit-prix-input"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 text-sm hover:bg-gray-50">
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[hsl(222,47%,11%)] text-white rounded-xl text-sm font-medium hover:bg-[hsl(222,47%,18%)] disabled:opacity-50"
                data-testid="save-produit-btn"
              >
                <Check size={14} />
                {saving ? 'Enregistrement...' : (editing ? 'Mettre à jour' : 'Créer le produit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
